import React from "react";
import { Play, Pause, LifeBuoy, Square } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

interface JamControlsProps {
  isPlaying: boolean;
  onToggle: () => void;
  onLost: () => void;
  onEnd: () => void;
  playbackRate: number;
  onRate: (r: number) => void;
  effectiveBpm: number;
}

export const JamControls: React.FC<JamControlsProps> = ({
  isPlaying,
  onToggle,
  onLost,
  onEnd,
  playbackRate,
  onRate,
  effectiveBpm,
}) => {
  const { t, tm } = useI18n();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        {/* LOST — discreet recovery trigger */}
        <button
          data-testid="recovery-mode-trigger-btn"
          onClick={onLost}
          className="flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-mono tracking-widest text-sky-200 transition active:scale-95"
          style={{ background: "rgba(56,189,248,0.08)", border: "1px solid rgba(56,189,248,0.22)" }}
        >
          <LifeBuoy size={15} />
          {tm("messages", "LOST")}
        </button>

        {/* Play / Pause */}
        <button
          data-testid="jam-play-pause-btn"
          onClick={onToggle}
          className="flex h-16 w-16 items-center justify-center rounded-full transition active:scale-95"
          style={{
            background: "linear-gradient(135deg,#FDE68A,#F59E0B 60%,#D97706)",
            boxShadow: "0 0 30px rgba(245,158,11,0.5)",
          }}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <Pause size={26} className="text-[#1a1204]" fill="#1a1204" />
          ) : (
            <Play size={26} className="text-[#1a1204] translate-x-0.5" fill="#1a1204" />
          )}
        </button>

        {/* End session */}
        <button
          data-testid="jam-end-session-btn"
          onClick={onEnd}
          className="flex items-center gap-2 rounded-full px-4 py-2.5 text-xs font-mono tracking-widest text-slate-300 transition active:scale-95"
          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)" }}
        >
          <Square size={13} />
          {tm("messages", "END")}
        </button>
      </div>

      {/* Tempo */}
      <div className="flex items-center gap-3">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{t("common.tempo")}</span>
        <input
          data-testid="jam-bpm-slider"
          type="range"
          min={0.8}
          max={1.2}
          step={0.01}
          value={playbackRate}
          onChange={(e) => onRate(parseFloat(e.target.value))}
          className="jam-slider flex-1"
        />
        <span className="font-mono text-[11px] text-amber-300 w-14 text-right">
          {Math.round(effectiveBpm)} BPM
        </span>
      </div>
    </div>
  );
};
